/**
 * Focus Animation Utilities
 * 
 * Pure computation functions for focus mode animation state.
 * Extracted from useFocusAnimation for maintainability.
 */

/**
 * Animation phase constants
 */
export const ENTERING_PHASES = [
  'entering-pan',
  'entering-fade-edges', 
  'entering-fade-nodes',
  'entering-move',
  'entering-zoom',
  'entering-show-edges'
];

export const EXITING_PHASES = [
  'exiting-fade-edges',
  'exiting-zoom',
  'exiting-move',
  'exiting-fade-nodes',
  'exiting-show-edges'
];

export const TRANSITION_PHASES = [
  'transition-fade-out',
  'transition-move',
  'transition-show-edges'
];

/**
 * Compute which positions to use based on animation phase
 * 
 * @param {Object} params
 * @param {string|null} params.animPhase - Current animation phase
 * @param {Array} params.tasks - All tasks
 * @param {Set} params.effectiveFocusedIds - IDs of focused tasks
 * @param {Object} params.displayPositions - Focus mode positions
 * @param {Object} params.nodePositions - Normal (non-focus) positions
 * @param {Object} params.lastFocusPositions - Positions from last focus session
 * @param {Object} params.snapshotPositions - Snapshot positions for transitions
 * @param {string|null} params.pendingAnimation - 'entering' | 'exiting' | null
 * @returns {Object} Position map { taskId: { x, y } }
 */
export function computePositionsToUse({
  animPhase,
  tasks,
  effectiveFocusedIds,
  displayPositions,
  nodePositions,
  lastFocusPositions,
  snapshotPositions,
  pendingAnimation,
  isFocusMode,
  wasInFocusMode
}) {
  const isPendingEnter = pendingAnimation === 'entering';
  const isPendingExit = pendingAnimation === 'exiting';
  
  // Use lastFocusPositions during exit animation (displayPositions is already back to normal)
  const focusPositionsToUse = lastFocusPositions || displayPositions;
  
  // If we're about to enter focus mode, keep nodes at their current positions
  if (isPendingEnter) {
    return nodePositions;
  }
  
  // CRITICAL: If focus mode just became active but animation hasn't started yet,
  // keep nodes at their original positions to prevent the "snap" effect.
  // This handles the first render after focusedTaskIds is set.
  if (isFocusMode && !wasInFocusMode && animPhase === null) {
    return nodePositions;
  }
  
  // If we're about to exit focus mode, keep nodes at focus positions momentarily
  if (isPendingExit) {
    return focusPositionsToUse;
  }
  
  // During pan and edge/node fade phases, keep nodes at ORIGINAL positions
  if (animPhase === 'entering-pan' || 
      animPhase === 'entering-fade-edges' || 
      animPhase === 'entering-fade-nodes') {
    return nodePositions;
  }
  
  // During move, zoom, and show-edges phases for entering, use focus positions for focused nodes
  if (animPhase === 'entering-move' || animPhase === 'entering-zoom' || animPhase === 'entering-show-edges') {
    const positions = {};
    tasks.forEach(task => {
      if (effectiveFocusedIds?.has(task.id)) {
        positions[task.id] = displayPositions[task.id] || nodePositions[task.id] || { x: 0, y: 0 };
      } else {
        // Non-focused stay at original (they're faded out anyway)
        positions[task.id] = nodePositions[task.id] || { x: 0, y: 0 };
      }
    });
    return positions;
  }
  
  // During exiting edge fade and zoom, keep at LAST focus positions (not displayPositions which is already normal)
  // Nodes stay at focus positions while we zoom out to show where they'll return to
  if (animPhase === 'exiting-fade-edges' || animPhase === 'exiting-zoom') {
    return focusPositionsToUse;
  }
  
  // During exiting move, fade-nodes, and show-edges, use normal positions
  if (animPhase === 'exiting-move' || animPhase === 'exiting-fade-nodes' || animPhase === 'exiting-show-edges') {
    return nodePositions;
  }
  
  // TRANSITION: During fade-out, keep nodes at SNAPSHOT positions (from before transition)
  if (animPhase === 'transition-fade-out') {
    return snapshotPositions || displayPositions;
  }
  
  // TRANSITION: During move and show-edges, use new focus positions
  if (animPhase === 'transition-move' || animPhase === 'transition-show-edges') {
    return displayPositions;
  }
  
  // Not animating: use display positions
  return displayPositions;
}

/**
 * Compute which edges to render and their fade state
 * 
 * @param {Object} params
 * @param {string|null} params.animPhase - Current animation phase
 * @param {Array} params.edges - All edges (normal mode)
 * @param {Array} params.displayEdges - Focus mode edges
 * @param {Set} params.effectiveFocusedIds - IDs of focused tasks (during animation)
 * @param {Set} params.allFocusedTaskIds - Current focused task IDs
 * @param {Array} params.lastFocusEdges - Edges from last focus session
 * @param {Array} params.snapshotEdges - Snapshot edges for transitions
 * @param {string|null} params.pendingAnimation - 'entering' | 'exiting' | null
 * @returns {Array} Edges with { ...edge, isFadingOut, isFadingIn }
 */
export function computeEdgesToRender({
  animPhase,
  edges,
  displayEdges,
  effectiveFocusedIds,
  allFocusedTaskIds,
  lastFocusEdges,
  snapshotEdges,
  pendingAnimation
}) {
  const isPendingEnter = pendingAnimation === 'entering';
  const isPendingExit = pendingAnimation === 'exiting';
  
  // Use lastFocusEdges during exit animation (displayEdges is already back to normal)
  const focusEdgesToUse = lastFocusEdges || displayEdges;
  
  // If we're about to enter focus mode, show only edges connected to focused nodes
  if (isPendingEnter) {
    return edges
      .filter(edge => allFocusedTaskIds?.has(edge.from) || allFocusedTaskIds?.has(edge.to))
      .map(edge => ({
        ...edge,
        isFadingOut: false,
        isFadingIn: false,
      }));
  }
  
  // If we're about to exit focus mode, show LAST focus edges (about to fade out)
  if (isPendingExit) {
    return focusEdgesToUse.map(edge => ({
      ...edge,
      isFadingOut: false,
      isFadingIn: false,
    }));
  }
  
  // ENTERING: During pan phase, show only edges connected to focused nodes
  if (animPhase === 'entering-pan') {
    return edges
      .filter(edge => effectiveFocusedIds?.has(edge.from) || effectiveFocusedIds?.has(edge.to))
      .map(edge => ({
        ...edge,
        isFadingOut: false,
        isFadingIn: false,
      }));
  }
  
  // ENTERING: Fade out edges (only those connected to focused nodes)
  if (animPhase === 'entering-fade-edges' || animPhase === 'entering-fade-nodes') {
    return edges
      .filter(edge => effectiveFocusedIds?.has(edge.from) || effectiveFocusedIds?.has(edge.to))
      .map(edge => ({
        ...edge,
        isFadingOut: true,
        isFadingIn: false,
      }));
  }
  
  // ENTERING: Nodes moving and zooming - keep edges hidden
  if (animPhase === 'entering-move' || animPhase === 'entering-zoom') {
    return [];
  }
  
  // ENTERING: Fade in edges at NEW positions
  if (animPhase === 'entering-show-edges') {
    return (displayEdges || []).map(edge => ({
      ...edge,
      isFadingOut: false,
      isFadingIn: true,
    }));
  }
  
  // EXITING: Fade out edges at LAST focus positions
  if (animPhase === 'exiting-fade-edges') {
    return focusEdgesToUse.map(edge => ({
      ...edge,
      isFadingOut: true,
      isFadingIn: false,
    }));
  }
  
  // EXITING: Zooming out, nodes moving, and fading in - keep edges hidden
  if (animPhase === 'exiting-zoom' || animPhase === 'exiting-move' || animPhase === 'exiting-fade-nodes') {
    return [];
  }
  
  // EXITING: Fade in all edges at original positions
  if (animPhase === 'exiting-show-edges') {
    return edges.map(edge => ({
      ...edge,
      isFadingOut: false,
      isFadingIn: true,
    }));
  }
  
  // TRANSITION: Fade out edges during fade-out phase
  if (animPhase === 'transition-fade-out') {
    const edgesToUse = snapshotEdges || displayEdges || [];
    return edgesToUse.map(edge => ({
      ...edge,
      isFadingOut: true,
      isFadingIn: false,
    }));
  }
  
  // TRANSITION: No edges during move
  if (animPhase === 'transition-move') {
    return [];
  }
  
  // TRANSITION: Fade in new edges
  if (animPhase === 'transition-show-edges') {
    return (displayEdges || []).map(edge => ({
      ...edge,
      isFadingOut: false,
      isFadingIn: true,
    }));
  }
  
  // Not animating - return displayEdges directly without creating new objects
  // This preserves referential equality and prevents unnecessary re-renders
  return displayEdges || [];
}

/**
 * Get animation state for a specific node
 * 
 * @param {Object} params
 * @param {string} params.taskId - Task ID to check
 * @param {string|null} params.animPhase - Current animation phase
 * @param {Set} params.effectiveFocusedIds - IDs of focused tasks
 * @param {Set} params.allFocusedTaskIds - Current focused task IDs
 * @param {Set} params.removingIds - IDs being removed in transition
 * @param {string|null} params.pendingAnimation - 'entering' | 'exiting' | null
 * @returns {{ isFadingOut: boolean, isFadingIn: boolean, isSliding: boolean }}
 */
export function getNodeAnimationStateForTask({
  taskId,
  animPhase,
  effectiveFocusedIds,
  allFocusedTaskIds,
  removingIds,
  pendingAnimation
}) {
  const isPendingEnter = pendingAnimation === 'entering';
  const isPendingExit = pendingAnimation === 'exiting';
  
  const isFocused = effectiveFocusedIds?.has(taskId);
  
  // During pending ENTER state, show everything normally (at original positions)
  if (isPendingEnter) {
    return { isFadingOut: false, isFadingIn: false, isSliding: false };
  }
  
  // During pending EXIT state, non-focused nodes should STAY hidden
  if (isPendingExit) {
    return { 
      isFadingOut: !isFocused,
      isFadingIn: false, 
      isSliding: false 
    };
  }
  
  // During pan phase, no fading yet
  if (animPhase === 'entering-pan') {
    return { isFadingOut: false, isFadingIn: false, isSliding: false };
  }
  
  // ENTERING: Non-focused nodes fade out during fade-nodes and stay hidden through zoom
  const shouldFadeOutNonFocused = animPhase === 'entering-fade-nodes' || 
                                   animPhase === 'entering-move' || 
                                   animPhase === 'entering-zoom' ||
                                   animPhase === 'entering-show-edges';
  
  // EXITING: Non-focused nodes fade in during fade-nodes phase
  const shouldFadeInNonFocused = animPhase === 'exiting-fade-nodes' || 
                                  animPhase === 'exiting-show-edges';
  
  // EXITING: Non-focused nodes are still hidden during zoom and move phases
  const shouldHideNonFocused = animPhase === 'exiting-fade-edges' || 
                                animPhase === 'exiting-zoom' ||
                                animPhase === 'exiting-move';
  
  const isFadingOut = !isFocused && (shouldFadeOutNonFocused || shouldHideNonFocused);
  const isFadingIn = !isFocused && shouldFadeInNonFocused;
  
  // Focused nodes slide during move phases
  // IMPORTANT: We set isSliding=true one phase BEFORE the actual move so that
  // the CSS transition class is applied before the position changes.
  // This ensures the transition animates smoothly instead of snapping.
  // For exiting, we set it during exiting-zoom (prep phase before exiting-move)
  const isSliding = isFocused && (
    animPhase === 'entering-fade-nodes' ||  // Prep for entering-move
    animPhase === 'entering-move' || 
    animPhase === 'exiting-zoom' ||         // Prep for exiting-move (zoom happens first)
    animPhase === 'exiting-move'
  );
  
  // TRANSITION: Nodes being removed fade out, remaining nodes slide
  if (animPhase?.startsWith('transition')) {
    const isBeingRemoved = removingIds?.has(taskId);
    const isInNewFocus = allFocusedTaskIds?.has(taskId);
    
    if (animPhase === 'transition-fade-out') {
      return { 
        isFadingOut: isBeingRemoved, 
        isFadingIn: false, 
        isSliding: false 
      };
    }
    
    if (animPhase === 'transition-move' || animPhase === 'transition-show-edges') {
      return { 
        isFadingOut: isBeingRemoved,
        isFadingIn: false, 
        isSliding: isInNewFocus && !isBeingRemoved
      };
    }
  }

  return { isFadingOut, isFadingIn, isSliding };
}

/**
 * Compute tasks to render during animation
 * 
 * PERFORMANCE OPTIMIZATION: Once non-focused nodes have faded out, we stop
 * rendering them entirely. This significantly improves performance with many nodes
 * because we're not animating/rendering 50+ invisible DOM elements.
 * 
 * @param {Object} params
 * @param {boolean} params.isAnimating - Whether any animation is running
 * @param {boolean} params.isTransitioning - Whether in transition animation
 * @param {string} params.animPhase - Current animation phase
 * @param {Array} params.tasks - All tasks
 * @param {Set} params.allFocusedTaskIds - Current focused task IDs
 * @param {Set} params.removingIds - IDs being removed in transition
 * @param {boolean} params.isPending - Whether animation is pending
 * @returns {Array|null} Tasks to render, or null to use displayTasks
 */
export function computeTasksToRender({
  isAnimating,
  isTransitioning,
  animPhase,
  tasks,
  allFocusedTaskIds,
  removingIds,
  isPending
}) {
  // During transition, show both old and new focused tasks
  if (isTransitioning) {
    return tasks.filter(t => 
      allFocusedTaskIds?.has(t.id) || removingIds?.has(t.id)
    );
  }
  
  // ENTERING focus mode: 
  // - During fade-out phases, render all tasks (they're fading out)
  // - After fade-out completes (entering-move onwards), only render focused tasks
  if (animPhase?.startsWith('entering')) {
    // These phases need all tasks visible (fading out)
    const needsAllTasks = animPhase === 'entering-pan' || 
                          animPhase === 'entering-fade-edges' || 
                          animPhase === 'entering-fade-nodes';
    
    if (needsAllTasks) {
      return tasks;
    }
    // After fade-out, only render focused tasks (huge perf win!)
    return tasks.filter(t => allFocusedTaskIds?.has(t.id));
  }
  
  // EXITING focus mode:
  // - During zoom/move phases, only render focused tasks
  // - During fade-in phases (exiting-fade-nodes onwards), render all tasks
  if (animPhase?.startsWith('exiting')) {
    const needsAllTasks = animPhase === 'exiting-fade-nodes' || 
                          animPhase === 'exiting-show-edges';
    
    if (needsAllTasks) {
      return tasks;
    }
    // During zoom-out and move, only render focused tasks
    return tasks.filter(t => allFocusedTaskIds?.has(t.id));
  }
  
  // Pending animation - show all tasks
  if (isPending) {
    return tasks;
  }
  
  // Other animation states - show all
  if (isAnimating) {
    return tasks;
  }
  
  return null; // Signal to use displayTasks
}

